import groupBy from 'lodash/groupBy';
import moment from 'moment';
import { getRequest } from '../api';

import {
  orderHistoryRequest,
  orderHistoryOrderDetailsRequest,
} from '../api/requestCreator';
import {
  formatOrderHistoryPayload,
  formatOrderDetailsPayload,
} from '../utils/format';
import {
  setOrderHistoryPageInfo,
  setOrderHistoryPageError,
  setOrderHistoryClicked,
  setOrderHistoryOrderDetailsClickError,
  setOrderHistoryPageInfoWithError,
} from '../analytics/gatherService';
import { ROUTES, ORDER_HISTORY_FILTERS, REDUCERS} from '../constants';
import { redirectToPath } from '../utils/window';
import { safelyAccessNestedProperties } from '../utils';

const currentYearString = moment().format('YYYY');
const lastYearString = moment()
  .subtract(1, 'years')
  .format('YYYY');

/* Input : map of filtered orders
 Returns the first orderId and filter of the most recent filter bucket */
export const getOrderIdBasedOnValidFilter = (filteredOrders: Object) => {
  if (filteredOrders[ORDER_HISTORY_FILTERS.SIX_MONTHS].length) {
    const { orderId } = filteredOrders[ORDER_HISTORY_FILTERS.SIX_MONTHS][0];
    return { orderId, currentFilter: ORDER_HISTORY_FILTERS.SIX_MONTHS };
  }
  if (filteredOrders[currentYearString].length) {
    const { orderId } = filteredOrders[currentYearString][0];
    return { orderId, currentFilter: currentYearString };
  }
  if (filteredOrders[lastYearString].length) {
    const { orderId } = filteredOrders[lastYearString][0];
    return { orderId, currentFilter: lastYearString };
  }
  if (filteredOrders[ORDER_HISTORY_FILTERS.ALL_TIME].length) {
    const { orderId } = filteredOrders[ORDER_HISTORY_FILTERS.ALL_TIME][0];
    return { orderId, currentFilter: ORDER_HISTORY_FILTERS.ALL_TIME };
  }
  return { orderId: null, currentFilter: ORDER_HISTORY_FILTERS.ALL_TIME };
};

export const filterOrdersByDate = (orders: Array) => {
  const filteredOrders = {
    [ORDER_HISTORY_FILTERS.SIX_MONTHS]: [],
    [currentYearString]: [],
    [lastYearString]: [],
    [ORDER_HISTORY_FILTERS.ALL_TIME]: [],
  };

  orders.map((order: Object) => {
    const sixMonthFilterDate = moment()
      .subtract(6, 'months')
      .subtract(1, 'days');
    const orderDate = moment(order.orderDate);
    if (orderDate.isAfter(sixMonthFilterDate)) {
      filteredOrders[ORDER_HISTORY_FILTERS.SIX_MONTHS].push(order);
    }
    if (orderDate.format('YYYY') === currentYearString) {
      filteredOrders[currentYearString].push(order);
    }
    if (orderDate.format('YYYY') === lastYearString) {
      filteredOrders[lastYearString].push(order);
    }
    return filteredOrders[ORDER_HISTORY_FILTERS.ALL_TIME].push(order);
  });

  return filteredOrders;
};

export const onOrderHistoryOrderDetailsSuccess = ({
  meta,
  payload,
}: Object = {}) => {
  const { dispatch, orderId, isClickExpansion } = meta;
  const {
    response: { data },
  } = payload;
  const { response, lineItems } = formatOrderDetailsPayload(data);
  dispatch({
    type: 'FETCH_ORDER_HISTORY_ORDER_DETAILS_SUCCESS',
    filter: REDUCERS.ORDER_DETAILS,
    payload: { response, lineItems, orderId },
  });
  dispatch({
    type: 'FETCH_ORDER_HISTORY_ORDER_META_SUCCESS',
    filter: REDUCERS.ORDER_HISTORY,
    payload: { orderId },
  });
  const lineItem =
    safelyAccessNestedProperties(
      [orderId, 'lineItems', 'lineItem'],
      response
    ) || [];
  const groupByFulfillment = groupBy(lineItem, (item: Object) =>
    item.fulfillment.fulfillmentMethod.toUpperCase()
  );
  if (isClickExpansion) {
    setOrderHistoryClicked(groupByFulfillment);
  } else {
    setOrderHistoryPageInfo(groupByFulfillment);
  }
};

export const onOrderHistoryOrderDetailsFailure = ({
  meta,
  error,
}: Object = {}) => {
  const { dispatch, orderId, isClickExpansion } = meta;
  const { errorData } = error.response.data;
  const errorMessage = errorData
    ? errorData.errors.error
    : [{ code: 'OS_COMORDERDETAILS_ERR_2000' }];
  const errorCode = errorMessage[0].code;

  dispatch({
    type: 'FETCH_ORDER_HISTORY_ORDER_META_ERROR',
    message: errorMessage,
    filter: REDUCERS.ORDER_HISTORY,
    payload: { orderId },
  });
  if (isClickExpansion) {
    setOrderHistoryOrderDetailsClickError();
  } else {
    setOrderHistoryPageInfoWithError(errorCode);
  }
};

export const fetchOrderHistoryOrderDetails = (
  orderId: String,
  isClickExpansion: Boolean = false
) => (dispatch: Function) => {
  dispatch({
    type: 'FETCH_ORDER_HISTORY_ORDER_META',
    filter: REDUCERS.ORDER_HISTORY,
    payload: {
      orderId,
    },
  });
  const { url, headers } = orderHistoryOrderDetailsRequest(orderId);
  getRequest(url, headers, true).then(
    (response: Object) => {
      onOrderHistoryOrderDetailsSuccess({
        meta: { dispatch, orderId, isClickExpansion },
        payload: { response },
      });
    },
    (error: Object) => {
      console.log('error', error);
      onOrderHistoryOrderDetailsFailure({
        meta: { dispatch, orderId, isClickExpansion },
        error,
      });
    }
  );
};

export const fetchViewReceiptOrderDetails = (orderId: String) => {
  const { url, headers } = orderHistoryOrderDetailsRequest(orderId);
  getRequest(url, headers, true).then(
    (response: Object) => response,
    (error: Object) => {
      console.log('error', error);
      return redirectToPath(ROUTES.PATH.ORDER_TRACKING);
    }
  );
};

export const onSuccess = ({ meta, payload }: Object = {}) => {
  const { dispatch } = meta;
  const {
    response: { data },
  } = payload;
  const { emailId, orders, orderActions } = formatOrderHistoryPayload(data);
  const filteredOrders = filterOrdersByDate(orders);
  const orderIdAndFilter = getOrderIdBasedOnValidFilter(filteredOrders);
  const { orderId, currentFilter } = orderIdAndFilter;
  dispatch({
    type: 'FETCH_ORDER_HISTORY_SUCCESS',
    filter: REDUCERS.ORDER_HISTORY,
    payload: { orders, emailId, orderActions, currentFilter, filteredOrders },
  });
  if (orderId) fetchOrderHistoryOrderDetails(orderId)(dispatch);
};

/**
 * function that gets called on api error response
 * @param {*} dispatch
 * @param {*} error
 * @param {*} request
 */
export const onFailure = ({ meta, error }: Object = {}) => {
  const { dispatch, history } = meta;
  console.log('ORDER HISTORY ERROR', error);

  if (error.response.status === 400) {
    return history.push(ROUTES.PATH.ORDER_TRACKING);
  }

  if (error.response.status === 401) {
    return redirectToPath(ROUTES.PATH.AUTH_SIGNIN_SUMMARY);
  }

  const errorResponse =
    safelyAccessNestedProperties(
      ['response', 'data', 'errorData', 'errors', 'error'],
      error
    ) || [];
  const ordersNotFound = errorResponse.filter(
    (errorCode: Object) => errorCode.code === 'OS_OTORDERHISTORY_ERR_2301'
  ).length;
  if (error.response.status === 404 && ordersNotFound) {
    const { emailId, orders, orderActions } = formatOrderHistoryPayload();
    const filteredOrders = filterOrdersByDate(orders);

    return dispatch({
      type: 'FETCH_ORDER_HISTORY_SUCCESS',
      filter: REDUCERS.ORDER_HISTORY,
      payload: {
        orders,
        emailId,
        orderActions,
        currentFilter: ORDER_HISTORY_FILTERS.ALL_TIME,
        filteredOrders,
      },
    });
  }

  dispatch({
    type: 'FETCH_ORDER_HISTORY_FAILURE',
    message: `We're sorry! A temporary connection error prevented your request from going through. Please try again.`,
    filter: REDUCERS.ORDER_HISTORY,
  });

  console.log('ORDER HISTORY ERROR', error);
  return setOrderHistoryPageError();
};

/**
 * middleware function that gets called directly from react component
 * dispatch action to trigger loading indicator
 * make api call
 * dispatch action based on api response
 * @param {*} history
 * @param {*} request - optional: { isInstore: false, isEmailLink: false }
 */

export const fetchOrderHistory = (history: Object) => (dispatch: Function) => {
  dispatch({
    type: 'FETCH_ORDER_HISTORY',
    filter: REDUCERS.ORDER_HISTORY,
  });

  const { url, headers } = orderHistoryRequest();

  getRequest(url, headers, true).then(
    (response: Object) => {
      onSuccess({
        meta: { dispatch },
        payload: { response },
      });
    },
    (error: Object) => {
      console.log('error', error);
      onFailure({ meta: { dispatch, history }, error });
    }
  );
};

export const toggleHistoryOrder = (orderId: String) => ({
  type: 'ORDER_HISTORY_DETAIL_TOGGLE',
  filter: REDUCERS.ORDER_HISTORY,
  payload: { orderId },
});

export const setOrderHistoryFilter = (currentFilter: string) => ({
  type: 'ORDER_HISTORY_DROPDOWN_SELECTION',
  filter: REDUCERS.ORDER_HISTORY,
  currentFilter,
});

export const setOrderHistoryPage = (currentPage: string) => ({
  type: 'ORDER_HISTORY_PAGE_SELECTION',
  filter: REDUCERS.ORDER_HISTORY,
  currentPage,
});

